import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, of, Observable, forkJoin } from 'rxjs';
import {
  switchMap,
  startWith,
  map,
  tap,
  filter,
  take,
  catchError,
  mergeMap,
} from 'rxjs/operators';

import { isObject } from '@plugins/shared/utils/assertFunctions';

import { translationKeyExists } from './helpers';
import {
  TranslationLoadType,
  TranslationStatusService,
} from './services/translation-status.service';

export interface TranslationsImport {
  default: Record<string, Record<string, unknown>>;
}

export class LibTranslationModule {
  protected translateService = inject(TranslateService);
  private translationStatusService = inject(TranslationStatusService);

  static trackedTranslationsKeys = new Set<string>([]);
  static loadedTranslations = new Set<string>([]);

  constructor(
    protected readonly translationsKey: string,
    protected readonly translationsLoader: (
      lang: string,
    ) => Observable<TranslationsImport>,
  ) {
    if (!this.isTranslationKeyBeingTracked(translationsKey)) {
      this.trackTranslationKey(translationsKey);
    }
  }

  private trackTranslationKey(translationsKey: string) {
    this.setTrackedTranslationKey(translationsKey);
    this.translateService.onLangChange
      .pipe(
        startWith({ lang: this.translateService.currentLang }),
        map(({ lang }) => lang),
        filter((lang) => !!lang),
        switchMap((lang) => {
          if (this.hasTranslationLanguageBeenLoaded(translationsKey, lang)) {
            return EMPTY;
          }
          this.setLoadedTranslation(this.translationsKey, lang);
          return of(lang);
        }),
        tap(() =>
          this.translationStatusService.onLoadStart({
            type: TranslationLoadType.LIB,
          }),
        ),
        switchMap((lang) => {
          const defaultLang = this.translateService.defaultLang;
          const loadDefault =
            defaultLang && this.translateService.defaultLang !== lang;
          return forkJoin([
            this.translationsLoader(lang).pipe(
              catchError(() => of({} as TranslationsImport)),
            ),
            loadDefault
              ? this.translationsLoader(defaultLang)
              : of({} as TranslationsImport),
          ]).pipe(
            switchMap(([translations, defaults]) => {
              if (translations.default || defaults.default) {
                return this.onTranslationsLoaded(
                  lang,
                  defaults.default
                    ? (this.mergeTranslations(
                        defaults.default,
                        translations.default,
                      ) as Record<string, Record<string, unknown>>)
                    : translations.default,
                );
              }
              return EMPTY;
            }),
          );
        }),
        tap(() =>
          this.translationStatusService.onLoadEnd({
            type: TranslationLoadType.LIB,
          }),
        ),
      )
      .subscribe();
  }

  private onTranslationsLoaded(
    lang: string,
    libTranslations: Record<string, Record<string, unknown>>,
  ) {
    return this.translationStatusService.loadingFeatures$.pipe(
      filter((loading) => !loading),
      take(1),
      mergeMap(() =>
        this.translateService.get(this.translationsKey).pipe(
          take(1),
          map(() => {
            const translations = this.translateService.translations as Record<
              string,
              object
            >;
            const currentLangTranslations = translations[lang] as
              | Record<string, object>
              | undefined;
            const overwrites = currentLangTranslations?.[this.translationsKey];
            if (
              isObject(overwrites) &&
              translationKeyExists(overwrites, this.translationsKey)
            ) {
              const overwrite = {
                [this.translationsKey]: this.mergeTranslations(
                  libTranslations[this.translationsKey],
                  overwrites,
                ),
              };
              return overwrite;
            }
            return libTranslations;
          }),
          tap((translations) => {
            this.translateService.setTranslation(lang, translations, true);
          }),
        ),
      ),
    );
  }

  private isTranslationKeyBeingTracked(translationsKey: string) {
    return LibTranslationModule.trackedTranslationsKeys.has(translationsKey);
  }

  private setTrackedTranslationKey(translationsKey: string) {
    LibTranslationModule.trackedTranslationsKeys.add(translationsKey);
  }

  private hasTranslationLanguageBeenLoaded(
    translationsKey: string,
    language: string,
  ) {
    return LibTranslationModule.loadedTranslations.has(
      `${translationsKey}_${language}`,
    );
  }

  private setLoadedTranslation(translationsKey: string, language: string) {
    LibTranslationModule.loadedTranslations.add(
      `${translationsKey}_${language}`,
    );
  }

  private mergeTranslations(
    target: Record<string, unknown>,
    source: Record<string, unknown>,
  ) {
    const output = Object.assign({}, target);
    if (isObject(source)) {
      for (const [key, val] of Object.entries(source)) {
        if (isObject(val) && isObject(target[key])) {
          output[key] = this.mergeTranslations(
            target[key] as Record<string, unknown>,
            val,
          );
        } else if (
          typeof target[key] === 'undefined' ||
          (!isObject(val) && !isObject(target[key]))
        ) {
          output[key] = val;
        }
      }
    }
    return output;
  }
}
